import * as Yup from 'yup';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Link,
  Stack,
  Alert,
  Checkbox,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
// routes
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { identifierSchema } from '../../../utils/authIdentifier';
//
import { MIconButton } from '../../@material-extend';

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const LoginSchema = Yup.object().shape({
    identifier: identifierSchema,
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      identifier: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        await login(values.identifier.trim(), values.password);
        enqueueSnackbar('Login success', {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        navigate(PATH_DASHBOARD.general.app);
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error) {
        console.error(error);
        resetForm();
        if (isMountedRef.current) {
          setSubmitting(false);
          const errorMsg =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            'Something went wrong';

          setErrors({ afterSubmit: errorMsg });
        }
      }
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}

          <TextField
            fullWidth
            size={isMobile ? 'small' : 'medium'}
            autoComplete="username"
            type="text"
            label="Email or phone number"
            placeholder="Enter email or phone number"
            {...getFieldProps('identifier')}
            sx={{
              input: { color: isMobile ? '#000000' : '#ffffff' },
              label: { color: '#4CAF7A' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#4CAF7A'
                },
                '&:hover fieldset': {
                  borderColor: '#4CAF7A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4CAF7A'
                }
              }
            }}
            error={Boolean(touched.identifier && errors.identifier)}
            helperText={touched.identifier && errors.identifier}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            size={isMobile ? 'small' : 'medium'}
            label="Password"
            {...getFieldProps('password')}
            sx={{
              input: { color: isMobile ? '#000000' : '#ffffff' },
              label: { color: '#4CAF7A' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#4CAF7A'
                },
                '&:hover fieldset': {
                  borderColor: '#4CAF7A'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4CAF7A'
                }
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 0 }}
          sx={{ my: 2 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isMobile ? 'text.secondary' : 'rgba(255, 255, 255, 0.72)',
              lineHeight: 1.6,
              pr: { xs: 0, sm: 2 },
            }}
          >
            Don&apos;t have an account?{' '}
            <Link
              component={RouterLink}
              to={PATH_AUTH.register}
              variant="subtitle2"
              underline="none"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                '&:hover': { color: 'primary.dark' },
              }}
            >
              Register
            </Link>
          </Typography>

          <Link
            component={RouterLink}
            variant="subtitle2"
            to={PATH_AUTH.resetPassword}
            sx={{
              color: 'primary.main',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              alignSelf: { xs: 'flex-start', sm: 'auto' },
            }}
          >
            Forgot password?
          </Link>
        </Stack>

        <LoadingButton fullWidth size={isMobile ? 'small' : 'large'} type="submit" variant="contained" loading={isSubmitting}>
          Login
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
